import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { ArrowLeft, ExternalLink, Link2, RotateCw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { SkillSourceBadge } from "@/components/skills/skill-source-badge";
import { useCreateJob, useDeleteSkill, useSkillDetail } from "@/lib/api/hooks";
import type { JobPriority } from "@/lib/mock/types";

export const Route = createFileRoute("/_authenticated/skills/$name")({
  component: SkillDetail,
  head: ({ params }) => ({ meta: [{ title: `${params.name} — Skill` }] }),
});

function SkillDetail() {
  const { name } = Route.useParams();
  const navigate = useNavigate();
  const { data: skill, isLoading, error } = useSkillDetail(name);
  const createJob = useCreateJob();
  const deleteSkill = useDeleteSkill();

  const [prompt, setPrompt] = useState("");
  const [priority, setPriority] = useState<JobPriority>("normal");
  const [timeout, setTimeoutSec] = useState(300);
  const [confirmDelete, setConfirmDelete] = useState(false);

  if (isLoading && !skill) {
    return (
      <div className="max-w-xl">
        <Button variant="ghost" size="sm" asChild className="mb-4">
          <Link to="/skills">
            <ArrowLeft className="h-4 w-4" /> Skills
          </Link>
        </Button>
        <p className="text-muted-foreground">Loading…</p>
      </div>
    );
  }

  if (error || !skill) {
    return (
      <div className="max-w-xl">
        <Button variant="ghost" size="sm" asChild className="mb-4">
          <Link to="/skills">
            <ArrowLeft className="h-4 w-4" /> Skills
          </Link>
        </Button>
        <p className="text-muted-foreground">{error?.message ?? "Skill not found."}</p>
      </div>
    );
  }

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!prompt.trim()) return;
    createJob.mutate(
      {
        skillName: skill.name,
        prompt,
        priority,
        timeoutSeconds: timeout,
        context: null,
      },
      {
        onSuccess: (job) => {
          toast.success("Job queued", {
            description: `${skill.name} · ${job.id.slice(0, 8)}`,
          });
          navigate({ to: "/jobs/$id", params: { id: job.id } });
        },
        onError: (err) => toast.error(err.message),
      },
    );
  };

  const onDelete = () => {
    deleteSkill.mutate(skill.name, {
      onSuccess: () => {
        toast.success("Skill deleted", { description: skill.name });
        navigate({ to: "/skills" });
      },
      onError: (err) => toast.error("Delete failed", { description: err.message }),
    });
  };

  const canDelete = skill.source !== "git";

  return (
    <div className="max-w-5xl space-y-6">
      <div>
        <Button variant="ghost" size="sm" asChild className="-ml-2 mb-3">
          <Link to="/skills">
            <ArrowLeft className="h-4 w-4" /> All skills
          </Link>
        </Button>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="font-display text-3xl font-semibold tracking-tight">
                {skill.display_name ?? skill.name}
              </h1>
              <SkillSourceBadge source={skill.source} visibility={skill.visibility} />
            </div>
            <code className="break-all font-mono text-xs text-muted-foreground">{skill.name}</code>
            {skill.description && (
              <p className="mt-3 max-w-2xl text-sm text-muted-foreground">{skill.description}</p>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <Button variant="outline" size="sm" asChild>
              <Link to="/skills/import">
                <RotateCw className="h-3.5 w-3.5" /> Re-import
              </Link>
            </Button>
            {canDelete && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setConfirmDelete(true)}
                className="text-destructive hover:bg-destructive/10 hover:text-destructive"
              >
                <Trash2 className="h-3.5 w-3.5" /> Delete
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base">Run skill</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={onSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="prompt">Prompt</Label>
                <Textarea
                  id="prompt"
                  rows={6}
                  maxLength={4000}
                  placeholder="What should the skill do?"
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                />
                <div className="text-right text-[11px] text-muted-foreground">
                  {prompt.length} / 4000
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Priority</Label>
                  <Select value={priority} onValueChange={(v) => setPriority(v as JobPriority)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="normal">Normal</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Timeout · {timeout}s</Label>
                  <Slider
                    min={30}
                    max={900}
                    step={30}
                    value={[timeout]}
                    onValueChange={([v]) => setTimeoutSec(v)}
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={!prompt.trim() || createJob.isPending}
                className="w-full sm:w-auto"
              >
                {createJob.isPending ? "Submitting…" : "Submit job"}
              </Button>
            </form>
          </CardContent>
        </Card>

        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Provenance</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <Row label="Source" value={skill.source} mono />
              {skill.source_ref && <Row label="Source ref" value={skill.source_ref} mono />}
              <Row label="Version" value={`v${skill.version}`} mono />
              <Row label="Visibility" value={skill.visibility} mono />
              {skill.owner_id && (
                <Row label="Owner" value={skill.owner_id.slice(0, 8) + "…"} mono />
              )}
              {skill.checksum && (
                <Row label="Checksum" value={skill.checksum.slice(0, 12) + "…"} mono />
              )}
              {skill.bundle_path && <Row label="Bundle" value={skill.bundle_path} mono />}
              {skill.updated_at && (
                <Row label="Updated" value={new Date(skill.updated_at).toLocaleString()} />
              )}
            </CardContent>
          </Card>

          {skill.files.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Bundle files ({skill.files.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="max-h-48 space-y-0.5 overflow-y-auto font-mono text-[11px] opacity-80">
                  {skill.files.map((path) => (
                    <li key={path}>{path}</li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {skill.references.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Link2 className="h-3.5 w-3.5 opacity-60" />
                  External references
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <p className="text-[11px] text-muted-foreground">
                  Paths the SKILL.md references but does not bundle. The host environment must
                  provide these.
                </p>
                <ul className="space-y-0.5 font-mono text-[11px]">
                  {skill.references.map((ref) => (
                    <li key={ref} className="flex items-center gap-1.5">
                      <ExternalLink className="h-3 w-3 shrink-0 opacity-60" />
                      <span className="truncate">{ref}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this skill?</AlertDialogTitle>
            <AlertDialogDescription>
              Removes <span className="font-mono">{skill.name}</span> from the catalog. Soft-delete
              only — the underlying record is retained for audit.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteSkill.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={onDelete}
              disabled={deleteSkill.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteSkill.isPending ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className={"break-all text-right text-xs font-medium" + (mono ? " font-mono" : "")}>
        {value}
      </span>
    </div>
  );
}
